import { useState } from 'react'
import { useBild } from '../lib/bild'
import type { Einreichung } from '../lib/warteschlangen'
import Bildlupe from './Bildlupe'
import Ortskarte from './Ortskarte'
import Nachbarn from './Nachbarn'

/**
 * `place_new` (§8.4): Koordinaten auf einer Karte, die mitgeschickten Bilder,
 * und die Nachbarn im Umkreis von 150 m, damit Dubletten auffallen.
 *
 * Die Bilder liegen unter `submissions/…` und **bleiben dort**. Nach der
 * Annahme legt ein Trigger die `core_media`-Zeilen an, mit dem Pfad
 * unverändert (§7.3).
 */

function zahl(wert: unknown): number | null {
  const n = Number(String(wert ?? '').trim())
  return Number.isFinite(n) ? n : null
}

export default function NeuerOrt({ e }: { e: Einreichung }) {
  // Welches Bild groß zu sehen ist. Über Ausstattung entscheidet man an
  // Einzelheiten, und die sind auf einem Vorschaubild nicht zu erkennen.
  const [gross, setGross] = useState<string | null>(null)
  const werte = e.payload?.werte ?? {}
  const lat = zahl(werte['lat'])
  const lon = zahl(werte['lon'])
  const name = typeof werte['name'] === 'string' ? werte['name'] : null
  const hinweis = typeof werte['hinweis'] === 'string' ? werte['hinweis'] : null
  const art = typeof werte['kind'] === 'string' ? werte['kind'] : 'playground'
  const bilder = Array.isArray(werte['bilder'])
    ? (werte['bilder'] as unknown[]).filter((b): b is string => typeof b === 'string')
    : []

  return (
    <>
      <div className="karte">
        <h3>{name ?? 'Neuer Ort ohne Namen'}</h3>
        <p className="gedaempft">
          Art: {art} · <span className="zahl">{lat?.toFixed(5) ?? '?'}</span>,{' '}
          <span className="zahl">{lon?.toFixed(5) ?? '?'}</span>
        </p>

        {hinweis && (
          <div className="hinweis-kasten">
            <p className="leise">Notiz des Einreichenden — wandert in keine Spalte:</p>
            <p>{hinweis}</p>
          </div>
        )}

        {lat !== null && lon !== null && <Ortskarte lat={lat} lon={lon} name={name} />}
      </div>

      {bilder.length > 0 && (
        <div className="karte">
          <h3>
            {bilder.length} {bilder.length === 1 ? 'Bild' : 'Bilder'} vor Ort
          </h3>
          <p className="leise">
            Werden nach der Annahme automatisch angelegt — die Dateien bleiben,
            wo sie liegen.
          </p>
          <div className="bilder-reihe">
            {bilder.map((pfad) => (
              <EntwurfsBild key={pfad} pfad={pfad} anklicken={() => setGross(pfad)} />
            ))}
          </div>
        </div>
      )}

      {lat !== null && lon !== null && (
        <Nachbarn
          lat={lat}
          lon={lon}
          leerText="Nichts in der Nähe — der Ort ist vermutlich neu."
        />
      )}

      {gross && <Bildlupe pfad={gross} schliessen={() => setGross(null)} />}
    </>
  )
}

function EntwurfsBild({ pfad, anklicken }: { pfad: string; anklicken: () => void }) {
  const bild = useBild(pfad)
  if (bild.art !== 'da') {
    return (
      <div className="bild-klein bild-klein-leer">
        <span className="leise">{bild.art === 'laedt' ? 'lädt …' : 'nicht abrufbar'}</span>
      </div>
    )
  }
  return (
    <button
      type="button"
      className="bild-knopf"
      onClick={anklicken}
      title="Groß ansehen"
    >
      <img className="bild-klein" src={bild.adresse} alt="Eingereichtes Bild" />
    </button>
  )
}
